"use client";

import { c as _c } from "react/compiler-runtime";
import { useState } from "react";
import { TerraLink } from "../TerraLink/TerraLink";
import { SimpleCarousel } from "../SimpleCarousel/SimpleCarousel";
import { VideoCovered } from "../VideoCovered/VideoCovered";
import { SpecialButton } from "../SpecialButton/SpecialButton";
import { CommonDescription } from "../CommonDescription/CommonDescription";
import { renderContentfulImage } from "../../utils/renderImage";
import { IMAGE_QUALITY_MOBILE } from "@/lib/heroImageUtils";
export interface InlineMediaPanelMediaItem {
  id?: string;
  type?: string;
  src?: string;
  alt?: string;
  focusArea?: string;
  /** Video-only: URL for video assets */
  videoURL?: string;
  assetURL?: string;
  /** Image-only: destination from the Image's attached `link` field */
  href?: string | null;
}
export interface InlineMediaPanel {
  id?: string;
  publicTitle?: string;
  description?: string;
  media?: InlineMediaPanelMediaItem[];
  buttons?: SpecialButton[];
  alignment?: "mediaRight" | "mediaLeft";
  displayMode?: string;
  colorSetting?: string;
  componentImagePriority?: "high" | "auto" | "low";
  isLCP?: boolean;
  isActiveSlide?: boolean;
}
const splitImgClasses = "t2-media-inline-panel__img rounded-[10px] w-full sm:w-[631px] sm:h-[354px] mx-auto block object-cover max-sm:rounded-none";
const fullWidthImgClasses = "t2-media-inline-panel__img rounded-[10px] w-full lg:w-[1060px] lg:h-[596px] mx-auto block object-cover max-sm:rounded-none";

// Keep in step with the width utilities above — these are what stop next/image
// falling back to a 1x/2x DPR srcset and serving the 3840px candidate to phones.
const splitImgSizes = "(min-width: 640px) 631px, 100vw";
const fullWidthImgSizes = "(min-width: 1024px) 1060px, 100vw";

// Video thumbnails need their own values: VideoCovered has no sm:w-[631px]
// equivalent, so in the split layout it stays viewport-wide until the media
// column narrows at lg. Reusing splitImgSizes here would under-request and
// render a blurry thumbnail between sm and lg.
const splitVideoSizes = "(min-width: 1024px) 631px, 100vw";
const fullWidthVideoSizes = "(min-width: 1060px) 1060px, 100vw";
const descriptionClasses = ["t2-layout__description", "text-gray-m9 t2-dark:text-white", "text-base leading-relaxed sm:text-lg sm:leading-8 tracking-[0.18px]", "[&_p]:mb-0 [&_li_p]:mb-0", "[&_ul]:list-disc [&_ul]:ps-10 [&_li]:mt-0"].join(" ");
export const InlineMediaPanel = t0 => {
  const $ = _c(48);
  const {
    id,
    description,
    media,
    buttons,
    alignment,
    displayMode,
    colorSetting,
    componentImagePriority,
    isLCP: t1,
    isActiveSlide
  } = t0;
  const isLCP = t1 === undefined ? false : t1;
  const isFullWidth = !description;
  const isMediaLeft = alignment === "mediaLeft";
  const isInCarousel = displayMode === "carousel";
  const formatMediaItem = _temp;
  const [innerActiveSlide, setInnerActiveSlide] = useState(0);
  let t2;
  if ($[0] !== componentImagePriority || $[1] !== isFullWidth || $[2] !== isLCP) {
    t2 = (mediaItem_0, index, t3, activeForSlide, t4) => {
      const useFullWidth = t3 === undefined ? false : t3;
      const enableLink = t4 === undefined ? false : t4;
      const mediaImage = formatMediaItem(mediaItem_0);
      if (mediaItem_0.type === "video") {
        return <VideoCovered key={`vc-${mediaItem_0.videoURL}-${index}`} videoId={mediaImage?.videoURL} image={mediaImage?.image} isNoDescription={isFullWidth} isActiveSlide={activeForSlide} sizes={useFullWidth ? fullWidthVideoSizes : splitVideoSizes} />;
      }
      const image = renderContentfulImage(mediaItem_0.src ?? "", mediaItem_0.alt ?? "", useFullWidth ? 1060 : 630, useFullWidth ? 596 : 350, useFullWidth ? fullWidthImgClasses : splitImgClasses, IMAGE_QUALITY_MOBILE, mediaItem_0.focusArea, componentImagePriority, isLCP, useFullWidth ? fullWidthImgSizes : splitImgSizes);
      if (enableLink && mediaItem_0.href) {
        return <div key={mediaItem_0.id || `img-${index}`}><TerraLink destination={mediaItem_0.href} linkType="text" disableStyles={true} className="block" ariaLabel={mediaItem_0.alt}>{image}</TerraLink></div>;
      }
      return <div key={mediaItem_0.id || `img-${index}`}>{image}</div>;
    };
    $[0] = componentImagePriority;
    $[1] = isFullWidth;
    $[2] = isLCP;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const renderMediaItem = t2;
  let t3;
  if ($[4] !== innerActiveSlide || $[5] !== isActiveSlide || $[6] !== isFullWidth || $[7] !== isInCarousel || $[8] !== media || $[9] !== renderMediaItem) {
    t3 = () => {
      if (!media || media.length === 0) {
        return null;
      }
      const isPanelActive = isActiveSlide !== false;
      if (media.length === 1) {
        return renderMediaItem(media[0], 0, isFullWidth, isActiveSlide, true);
      }
      if (isInCarousel) {
        return renderMediaItem(media[0], 0, isFullWidth, isActiveSlide);
      }
      return <SimpleCarousel onSlideChange={setInnerActiveSlide}>{media.map((item, i) => renderMediaItem(item, i, isFullWidth, isPanelActive && i === innerActiveSlide))}</SimpleCarousel>;
    };
    $[4] = innerActiveSlide;
    $[5] = isActiveSlide;
    $[6] = isFullWidth;
    $[7] = isInCarousel;
    $[8] = media;
    $[9] = renderMediaItem;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  const renderMedia = t3;
  let t4;
  if ($[11] !== buttons || $[12] !== colorSetting) {
    t4 = () => {
      if (!buttons || buttons.length === 0) {
        return null;
      }
      return <div className="flex items-center gap-5 sm:gap-[50px] mt-8 sm:mt-[60px] px-2.5 sm:px-0">{buttons.map((btn, index_0) => {
          if (btn.primary) {
            return <TerraLink key={`btn-${btn.label}-${index_0}`} tabIndex={0} primary={btn.primary} label={btn.label} destination={btn.destination} />;
          }
          return <SpecialButton key={`sp-${btn.label}-${index_0}`} label={btn.label} destination={btn.destination} size={btn.size} iconColor={buttons.length > 1 ? "dark" : "light"} isDarkTheme={colorSetting === "dark"} />;
        })}</div>;
    };
    $[11] = buttons;
    $[12] = colorSetting;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  const renderPanelButtons = t4;
  if (isFullWidth) {
    const t5 = `wimp-${id}`;
    const t6 = isInCarousel ? "lg:h-[625px] lg:flex lg:items-center" : "";
    let t7;
    if ($[14] !== renderMedia) {
      t7 = renderMedia();
      $[14] = renderMedia;
      $[15] = t7;
    } else {
      t7 = $[15];
    }
    let t8;
    if ($[16] !== t7) {
      t8 = <div className="t2-media-inline-panel__media--no-description mx-auto w-full lg:max-w-[1060px]">{t7}</div>;
      $[16] = t7;
      $[17] = t8;
    } else {
      t8 = $[17];
    }
    let t9;
    if ($[18] !== t6 || $[19] !== t8) {
      t9 = <div className={t6}>{t8}</div>;
      $[18] = t6;
      $[19] = t8;
      $[20] = t9;
    } else {
      t9 = $[20];
    }
    let t10;
    if ($[21] !== renderPanelButtons) {
      t10 = renderPanelButtons();
      $[21] = renderPanelButtons;
      $[22] = t10;
    } else {
      t10 = $[22];
    }
    let t11;
    if ($[23] !== t10 || $[24] !== t5 || $[25] !== t9) {
      t11 = <div id={t5} className="w-full">{t9}{t10}</div>;
      $[23] = t10;
      $[24] = t5;
      $[25] = t9;
      $[26] = t11;
    } else {
      t11 = $[26];
    }
    return t11;
  }
  const t5 = `wimp-${id}`;
  const t6 = isMediaLeft ? "lg:flex-row-reverse" : "";
  const t7 = isInCarousel ? "lg:py-[50px]" : "";
  let t8;
  if ($[27] !== t6 || $[28] !== t7) {
    t8 = ["flex flex-col-reverse gap-0", "lg:flex-row lg:justify-between lg:gap-16 xl:gap-[100px]", t6, t7].filter(Boolean);
    $[27] = t6;
    $[28] = t7;
    $[29] = t8;
  } else {
    t8 = $[29];
  }
  const t9 = t8.join(" ");
  let t10;
  if ($[30] !== description) {
    t10 = <CommonDescription description={description} className={descriptionClasses} />;
    $[30] = description;
    $[31] = t10;
  } else {
    t10 = $[31];
  }
  let t11;
  if ($[32] !== renderPanelButtons) {
    t11 = renderPanelButtons();
    $[32] = renderPanelButtons;
    $[33] = t11;
  } else {
    t11 = $[33];
  }
  let t12;
  if ($[34] !== t10 || $[35] !== t11) {
    t12 = <div className="w-full lg:flex-1 pt-8 lg:pt-0 px-5 lg:px-0">{t10}{t11}</div>;
    $[34] = t10;
    $[35] = t11;
    $[36] = t12;
  } else {
    t12 = $[36];
  }
  let t13;
  if ($[37] !== renderMedia) {
    t13 = renderMedia();
    $[37] = renderMedia;
    $[38] = t13;
  } else {
    t13 = $[38];
  }
  let t14;
  if ($[39] !== t13) {
    t14 = <div className="t2-media-inline-panel__media relative w-full lg:max-w-[631px] lg:flex-shrink-0">{t13}</div>;
    $[39] = t13;
    $[40] = t14;
  } else {
    t14 = $[40];
  }
  let t15;
  if ($[41] !== t12 || $[42] !== t14 || $[43] !== t9) {
    t15 = <div className={t9}>{t12}{t14}</div>;
    $[41] = t12;
    $[42] = t14;
    $[43] = t9;
    $[44] = t15;
  } else {
    t15 = $[44];
  }
  let t16;
  if ($[45] !== t15 || $[46] !== t5) {
    t16 = <div id={t5} className="w-full">{t15}</div>;
    $[45] = t15;
    $[46] = t5;
    $[47] = t16;
  } else {
    t16 = $[47];
  }
  return t16;
};
function _temp(mediaItem) {
  if (mediaItem?.type === "video") {
    const hasThumbnail = Boolean(mediaItem.assetURL);
    return {
      videoURL: mediaItem.videoURL,
      image: hasThumbnail ? {
        url: mediaItem.assetURL as string,
        alt: mediaItem.alt || ""
      } : undefined
    };
  }
  if (mediaItem?.type === "Image") {
    return {
      image: {
        url: mediaItem.src || "",
        alt: mediaItem.alt || ""
      }
    };
  }
  return null;
}